/**
 * @fileoverview Navigation Flow Hook
 *
 * Manages step navigation state for the case ingestion wizard including:
 * - Current step index with URL synchronization
 * - Step navigation guards (prevent jumping without matter)
 * - Sidebar navigation with pending save handling
 * - Browser back/forward support via URL params
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CREATION_STEP_COUNT,
  FIRST_INGESTION_STEP_INDEX,
  UNIFIED_STEPS,
} from '../constants';

/** Find step index by id, with fallback */
const getStepIndexById = (
  stepId: string | null,
  fallbackIndex: number,
): number => {
  if (!stepId) return fallbackIndex;
  const step = UNIFIED_STEPS.find((s) => s.id === stepId);
  return step?.index ?? fallbackIndex;
};

/** Get step id by index */
const getStepIdByIndex = (index: number): string => {
  return UNIFIED_STEPS[index]?.id ?? 'basics';
};

export interface UseNavigationFlowReturn {
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** True when saving before navigating via stepper */
  isSavingStep: boolean;
  /** Navigate directly to a step (no save trigger) */
  goToStep: (index: number) => void;
  /** Navigate to previous step */
  goBack: () => void;
  /** Navigate to next step (simple increment) */
  goToNextStep: () => void;
  /** Set step index directly (for use by parent context) */
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Request navigation to a step from sidebar.
   * For audit-tracked steps, triggers form submission first.
   */
  requestNavigateToStep: (targetIndex: number) => void;
  /**
   * Called by step components after save completes.
   * Handles pending navigation if sidebar triggered the save.
   */
  onSaveComplete: (success: boolean) => void;
}

interface UseNavigationFlowOptions {
  /** Initial matter ID from URL param (for determining start step) */
  initialMatterId: string | null;
  /** Current matter ID (may change after creation) */
  matterId: string | null;
}

/**
 * Hook to manage step navigation state and URL synchronization.
 *
 * Features:
 * - Syncs current step with URL query param (?step=stepId)
 * - Handles browser back/forward navigation
 * - Guards against jumping to ingestion steps without a matter
 * - Supports save-before-navigate pattern for sidebar clicks
 *
 * @param options - Configuration options
 */
export const useNavigationFlow = ({
  initialMatterId,
  matterId,
}: UseNavigationFlowOptions): UseNavigationFlowReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize step from URL, with intelligent fallback
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const stepFromUrl = searchParams.get('step');
    const fallback = initialMatterId ? FIRST_INGESTION_STEP_INDEX : 0;
    return getStepIndexById(stepFromUrl, fallback);
  });

  // Track the last step we wrote to URL to avoid reacting to our own changes
  const lastWrittenStepId = useRef<string | null>(null);
  // Track the last URL step we processed to detect actual browser navigation
  const lastProcessedUrlStep = useRef<string | null>(null);

  // Sidebar navigation state
  const [isSavingStep, setIsSavingStep] = useState(false);
  const isSavingStepRef = useRef(false);
  const pendingNavigationTarget = useRef<number | null>(null);

  // ==========================================================================
  // URL Synchronization
  // ==========================================================================

  // Sync step index → URL (replace to avoid polluting history)
  useEffect(() => {
    const stepId = getStepIdByIndex(currentStepIndex);
    lastWrittenStepId.current = stepId;
    lastProcessedUrlStep.current = stepId;
    setSearchParams({ step: stepId }, { replace: true });
  }, [currentStepIndex, setSearchParams]);

  // Sync URL → step index (browser back/forward only)
  // This effect should ONLY handle actual browser navigation (back/forward buttons)
  useEffect(() => {
    const stepFromUrl = searchParams.get('step');

    // Ignore empty step params after initial mount to avoid oscillation
    if (!stepFromUrl) return;

    // Skip if URL hasn't actually changed (prevents reacting to stale searchParams)
    if (stepFromUrl === lastProcessedUrlStep.current) return;

    // Skip if this is our own programmatic write
    if (stepFromUrl === lastWrittenStepId.current) return;

    // Update our tracking
    lastProcessedUrlStep.current = stepFromUrl;

    const urlIndex = getStepIndexById(stepFromUrl, 0);

    // Don't allow jumping to ingestion steps without a matter
    if (urlIndex >= CREATION_STEP_COUNT && !matterId) return;

    setCurrentStepIndex(urlIndex);
  }, [searchParams, matterId]);

  // ==========================================================================
  // Navigation Functions
  // ==========================================================================

  /**
   * Navigate directly to a specific step.
   * Guards against invalid indices and jumping without a matter.
   */
  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= UNIFIED_STEPS.length) return;
      // Don't allow jumping to ingestion steps without a matterId
      if (index >= CREATION_STEP_COUNT && !matterId) return;
      setCurrentStepIndex(index);
    },
    [matterId],
  );

  /**
   * Navigate to the previous step.
   */
  const goBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  /**
   * Navigate to the next step (simple increment).
   * Use this for steps 2+ where no special logic is needed.
   */
  const goToNextStep = useCallback(() => {
    if (currentStepIndex < UNIFIED_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [currentStepIndex]);

  /**
   * Request navigation to a specific step from sidebar.
   * For audit-tracked steps, triggers form submission first.
   * The form's onSaveComplete callback will handle navigation.
   */
  const requestNavigateToStep = useCallback(
    (targetIndex: number) => {
      if (targetIndex < 0 || targetIndex >= UNIFIED_STEPS.length) return;
      if (targetIndex >= CREATION_STEP_COUNT && !matterId) return;
      if (targetIndex === currentStepIndex) return;
      if (isSavingStep || isSavingStepRef.current) return;

      const isCurrentStepAuditTracked =
        currentStepIndex >= FIRST_INGESTION_STEP_INDEX;

      if (isCurrentStepAuditTracked) {
        isSavingStepRef.current = true;
        setIsSavingStep(true);
        pendingNavigationTarget.current = targetIndex;
        const form = document.getElementById(
          'ingestion-step-form',
        ) as HTMLFormElement | null;
        if (form) {
          form.requestSubmit();
          return; // onSaveComplete will navigate
        }
        isSavingStepRef.current = false;
        setIsSavingStep(false);
      }

      setCurrentStepIndex(targetIndex);
    },
    [matterId, currentStepIndex, isSavingStep],
  );

  /**
   * Called by step components after save completes.
   * - If sidebar triggered navigation: goes to that target step
   * - If Continue button triggered: goes to next step
   * - On failure: stays on current step
   */
  const onSaveComplete = useCallback(
    (success: boolean) => {
      const target = pendingNavigationTarget.current;
      pendingNavigationTarget.current = null;
      isSavingStepRef.current = false;
      setIsSavingStep(false);

      if (success) {
        if (target !== null) {
          // Explicit navigation target from sidebar click
          setCurrentStepIndex(target);
        } else if (currentStepIndex < UNIFIED_STEPS.length - 1) {
          // No explicit target (Continue button) - go to next step
          setCurrentStepIndex(currentStepIndex + 1);
        }
      }
      // On failure: stay on current step
    },
    [currentStepIndex],
  );

  return {
    currentStepIndex,
    isSavingStep,
    goToStep,
    goBack,
    goToNextStep,
    setCurrentStepIndex,
    requestNavigateToStep,
    onSaveComplete,
  };
};
